/**
 * @file Form Element
 * @author chenqiang(chenqiang03)
 */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { useMemo } from 'react';
import classNames from 'classnames';
import FieldForm, { List, useWatch } from 'rc-field-form';
import { ConfigContext } from '../config-provider';
import { FormContext } from './context';
import useForm, { FormInstance } from './hooks/useForm';
import SizeContext, { SizeContextProvider } from '../config-provider/SizeContext';
const InternalForm = (props, ref) => {
    const contextSize = React.useContext(SizeContext);
    const { getPrefixCls, direction, form: contextForm } = React.useContext(ConfigContext);
    const { prefixCls: customizePrefixCls, className = '', cols = 1, size = contextSize, form, colon, labelAlign, labelCol, labelWidth, inputMaxWidth = 400, wrapperCol, hideRequiredMark, layout = 'horizontal', scrollToFirstError = true, requiredMark, keepDisplayExtra, onFinishFailed, name } = props, restFormProps = __rest(props, ["prefixCls", "className", "cols", "size", "form", "colon", "labelAlign", "labelCol", "labelWidth", "inputMaxWidth", "wrapperCol", "hideRequiredMark", "layout", "scrollToFirstError", "requiredMark", "keepDisplayExtra", "onFinishFailed", "name"]);
    const mergedRequiredMark = useMemo(() => {
        if (requiredMark !== undefined) {
            return requiredMark;
        }
        if (contextForm && contextForm.requiredMark !== undefined) {
            return contextForm.requiredMark;
        }
        if (hideRequiredMark) {
            return false;
        }
        return true;
    }, [hideRequiredMark, requiredMark, contextForm]);
    const mergedKeepDisplayExtra = useMemo(() => {
        if (keepDisplayExtra !== undefined) {
            return keepDisplayExtra;
        }
        if (contextForm && contextForm.keepDisplayExtra !== undefined) {
            return contextForm.keepDisplayExtra;
        }
        return false;
    }, [keepDisplayExtra, contextForm]);
    const prefixCls = getPrefixCls('form', customizePrefixCls);
    const formClassName = classNames(prefixCls, {
        [`${prefixCls}-${layout}`]: true,
        [`${prefixCls}-col-${cols}`]: layout !== 'vertical',
        [`${prefixCls}-hide-required-mark`]: mergedRequiredMark === false,
        [`${prefixCls}-rtl`]: direction === 'rtl',
        [`${prefixCls}-${size}`]: size
    }, className);
    const [wrapForm] = useForm(form);
    const __INTERNAL__ = wrapForm.__INTERNAL__;
    __INTERNAL__.name = name;
    const formContextValue = useMemo(() => ({
        name,
        cols,
        labelAlign,
        labelCol,
        labelWidth,
        inputMaxWidth,
        wrapperCol,
        vertical: layout === 'vertical',
        colon,
        requiredMark: mergedRequiredMark,
        keepDisplayExtra: mergedKeepDisplayExtra,
        itemRef: __INTERNAL__.itemRef
    }), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        name, cols, labelAlign, labelCol,
        labelWidth, inputMaxWidth, wrapperCol,
        layout, colon, mergedRequiredMark, mergedKeepDisplayExtra
    ]);
    React.useImperativeHandle(ref, () => wrapForm);
    const onInternalFinishFailed = (errorInfo) => {
        onFinishFailed === null || onFinishFailed === void 0 ? void 0 : onFinishFailed(errorInfo);
        let defaultScrollToFirstError = { block: 'nearest' };
        if (scrollToFirstError && errorInfo.errorFields.length) {
            if (typeof scrollToFirstError === 'object') {
                defaultScrollToFirstError = scrollToFirstError;
            }
            wrapForm.scrollToField(errorInfo.errorFields[0].name, defaultScrollToFirstError);
        }
    };
    return (<SizeContextProvider size={size}>
            <FormContext.Provider value={formContextValue}>
                <FieldForm id={name} {...restFormProps} name={name} onFinishFailed={onInternalFinishFailed} form={wrapForm} className={formClassName}/>
            </FormContext.Provider>
        </SizeContextProvider>);
};
const Form = React.forwardRef(InternalForm);
export { useForm, useWatch, List, FormInstance };
export default Form;
